import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import type { BacktestInvoiceRow } from "@/types/backtest";
import { formatCurrency } from "@/lib/currency";

interface MonthlyData {
  month: string;
  missed: number;
  captured: number;
}

function buildMonthlyData(rows: BacktestInvoiceRow[]): MonthlyData[] {
  const byMonth: Record<string, { missed: number; captured: number }> = {};

  for (const row of rows) {
    if (!row.was_eligible || !row.invoice_date) continue;
    const d = new Date(row.invoice_date);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    if (!byMonth[key]) byMonth[key] = { missed: 0, captured: 0 };
    byMonth[key].missed += row.missed_savings;
    byMonth[key].captured += row.captured_savings;
  }

  return Object.entries(byMonth)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, vals]) => {
      const [year, month] = key.split("-");
      const label = new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString("en-US", {
        month: "short",
        year: "2-digit",
      });
      return { month: label, ...vals };
    });
}

interface Props {
  rows: BacktestInvoiceRow[];
}

export function MonthlySavingsChart({ rows }: Props) {
  const data = buildMonthlyData(rows);

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-sm text-muted-foreground">
        No eligible invoices to chart.
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
        <YAxis
          tick={{ fontSize: 11 }}
          tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
          width={52}
        />
        <Tooltip
          formatter={(value: number, name: string) => [
            formatCurrency(value),
            name === "missed" ? "Missed" : "Captured",
          ]}
          contentStyle={{ fontSize: 12 }}
        />
        <Legend
          formatter={(value) => (value === "missed" ? "Missed" : "Captured")}
          wrapperStyle={{ fontSize: 12 }}
        />
        <Bar dataKey="missed" fill="hsl(var(--destructive))" opacity={0.8} radius={[2, 2, 0, 0]} />
        <Bar
          dataKey="captured"
          fill="hsl(var(--success, 142 76% 36%))"
          opacity={0.8}
          radius={[2, 2, 0, 0]}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}
